'use client';

import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import type { DraggableProvidedDragHandleProps } from '@hello-pangea/dnd';
import { WorkflowAccordion } from './workflow-accordion';
import { WorkflowBookmark } from './workflow-bookmark';
import {
  handleDragEnd,
  isVisibleWorkflowFolder,
} from './utils/drag-and-drop-utils';
import type { Folder } from '@/lib/bookmark/types';
import { cn } from '@/lib/cn';
import { useWorkflows } from '@/providers/workflow-provider';
import { useEffect, useMemo } from 'react';

interface WorkflowsProps {
  readonly workEngagementId?: string | null;
  readonly isEditMode?: boolean;
  readonly isDashboard?: boolean;
  readonly isLastGroup?: boolean;
}

export const Workflows = ({
  workEngagementId = null,
  isEditMode = false,
  isDashboard = false,
  isLastGroup = false,
}: WorkflowsProps) => {
  const {
    setIsDashboard,
    userWorkflows,
    workEngagementWorkflows,
    setWorkflows,
    save,
  } = useWorkflows();

  // When rendering for a specific work engagement, use that workflow
  // Otherwise use user workflows
  const workflows = useMemo(() => {
    if (workEngagementId) {
      return workEngagementWorkflows?.find(
        (w) => w.work_engagement_id === workEngagementId
      );
    }
    return userWorkflows;
  }, [workEngagementId, workEngagementWorkflows, userWorkflows]);

  useEffect(() => {
    setIsDashboard(isDashboard);
  }, [isDashboard, setIsDashboard]);

  const visibleFolders = useMemo(() => {
    return (workflows?.folders ?? []).filter(isVisibleWorkflowFolder);
  }, [workflows?.folders]);

  const hiddenFolders = useMemo(() => {
    if (isDashboard) return [];
    return (workflows?.folders ?? []).filter(
      (f) => f.kind === 'workflow' && !f.pinned
    );
  }, [workflows?.folders, isDashboard]);

  const lastFolderId =
    hiddenFolders.length > 0
      ? hiddenFolders[hiddenFolders.length - 1].client_id
      : visibleFolders[visibleFolders.length - 1]?.client_id;

  const renderFolderRow = (
    folder: Folder,
    dragHandleProps: DraggableProvidedDragHandleProps | null
  ) => (
    <WorkflowAccordion
      workEngagementId={workEngagementId}
      isEditMode={isEditMode}
      folder={folder}
      isDashboard={isDashboard}
      isLastFolder={isLastGroup && folder.client_id === lastFolderId}
      dragHandleProps={dragHandleProps}
    >
      {(scrollContainerRef) => (
        <DragDropContext
          onDragEnd={(result) =>
            void handleDragEnd({
              type: 'bookmark',
              result,
              workflows,
              setWorkflows,
              save,
            })
          }
        >
          <Droppable
            isDropDisabled={!isEditMode}
            droppableId={folder.client_id}
            direction='horizontal'
          >
            {(droppableProvided) => (
              <div
                {...droppableProvided.droppableProps}
                ref={(el) => {
                  droppableProvided.innerRef(el);
                  scrollContainerRef.current = el;
                }}
                className='flex w-fit max-w-full flex-nowrap overflow-x-auto rounded-lg border shadow-card'
              >
                {(folder.child_bookmarks ?? []).length === 0 ? (
                  <p className='text-xs text-muted-foreground'>
                    Det finns inga sparade vyer under detta flöde.
                  </p>
                ) : (
                  (folder.child_bookmarks ?? []).map(
                    (bookmark, bookmarkIndex) => {
                      return (
                        <Draggable
                          key={bookmark.client_id}
                          draggableId={bookmark.client_id}
                          index={bookmarkIndex}
                          isDragDisabled={!isEditMode}
                        >
                          {(draggableProvided) => (
                            <div
                              ref={draggableProvided.innerRef}
                              {...draggableProvided.draggableProps}
                              className={cn({
                                'border-l': bookmarkIndex !== 0,
                              })}
                            >
                              <WorkflowBookmark
                                workEngagementId={workEngagementId}
                                isEditMode={isEditMode}
                                bookmark={bookmark}
                                dragHandleProps={
                                  draggableProvided.dragHandleProps
                                }
                              />
                            </div>
                          )}
                        </Draggable>
                      );
                    }
                  )
                )}
                {droppableProvided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}
    </WorkflowAccordion>
  );

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex flex-col'>
        <DragDropContext
          onDragEnd={(result) =>
            void handleDragEnd({
              type: 'folder',
              result,
              workflows,
              setWorkflows,
              save,
            })
          }
        >
          <Droppable isDropDisabled={!isEditMode} droppableId='workflows'>
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                className='flex flex-col'
              >
                {workflows &&
                  visibleFolders.map((folder, index) => {
                    return (
                      <Draggable
                        key={folder.client_id}
                        draggableId={folder.client_id}
                        index={index}
                        isDragDisabled={!isEditMode}
                      >
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                          >
                            {renderFolderRow(folder, provided.dragHandleProps)}
                          </div>
                        )}
                      </Draggable>
                    );
                  })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
        {workflows && hiddenFolders.length > 0 && (
          <>
            <div className='border-b bg-subtle/30 px-4 py-2 text-xs text-muted-foreground'>
              Dolda · {hiddenFolders.length}
            </div>
            {hiddenFolders.map((folder) => (
              <div key={folder.client_id}>{renderFolderRow(folder, null)}</div>
            ))}
          </>
        )}
      </div>
    </div>
  );
};
